<template>
  <v-container fluid justify-start>
    <loading
      slot="activator"
      :active.sync="isDeleting"
      :can-cancel="false"
      loader="dots"
      :color="appColor_Progress"
    />

    <!-- Data Table -->
    <v-data-table
      :headers="headers"
      :items="keyTags"
      :loading="isLoading"
      :search="search"
      :custom-filter="customFilter"
      :no-data-text="noDataText"
      item-key="id"
      :loading-text="getExpression('Alert', 'LoadingItems')"
      :footer-props="{
        'items-per-page-text': getExpression('Label', 'RowsPerPage')
      }"
      class="elevation-2"
      >
      <v-progress-linear slot="progress" :color="appColor_Progress" indeterminate />

      <template v-slot:top>

        <!-- Header Toolbar -->
        <v-toolbar dense flat dark :color="toolbarColor">
         
          <app-back-button v-bind:color="toolbarColorDark" />
         
          <v-toolbar-title :class="titleClass">Kiosk Key/Tag Mappings</v-toolbar-title>

          <v-spacer />

          <!-- Edit Dialog -->
          <v-toolbar-items>
            <v-dialog
              v-model="editDialog"
              :max-width="dialogWidth"
              :persistent="true">

              <template v-slot:activator="{ on }">
                <v-btn v-if="isSmallDevice"
                  small
                  slot="activator"
                  :disabled="isLoading"
                  :color="toolbarColorDark"
                  class="mb-1 mt-0 mr-0 elevation-0"
                  v-on="on"
                  >New
                </v-btn>
                <v-btn v-else
                  slot="activator"
                  :disabled="isLoading"
                  :color="toolbarColorDark"
                  class="mb-1 mt-0 mr-0 elevation-0"
                  v-on="on"
                  >Create Mapping
                </v-btn>
              </template>

              <v-card>
                <v-toolbar dense dark flat :color="toolbarColor">
                  <v-toolbar-title>
                    <v-icon class="mr-2">edit</v-icon>
                      {{ formTitle }}
                  </v-toolbar-title>
                </v-toolbar>

                <v-card-text class="pb-0 px-2">
                  <v-container grid-list-md>
                    <form>

                      <v-row no-gutters>

                        <!-- Kiosk -->
                        <v-col cols="12" sm="12">
                          <kiosk-combobox
                            v-model="editedKeyTag.kioskId"
                            :options="selectorKiosks"
                            :placeholder="'Select kiosk'"
                            :label="getExpression('Label', 'Kiosk')"
                            :disabled="editedKeyTag.residentId > 0 && editedIndex >= 0"
                            v-validate="'min_value:1'"
                            :error="errors.first('kioskId')"
                            data-vv-name="kioskId"
                            ref="kiosk"
                            class="mb-4"
                          />
                        </v-col>
                      </v-row>

                      <v-row no-gutters>

                        <!-- RFID Tag -->
                        <v-col cols="12" sm="7">
                          <v-text-field
                            v-model="editedKeyTag.tagId"
                            label="RFID Tag"
                            hide-details="auto"
                            :error-messages="errors.first('tagId')"
                            v-validate="'required|min:10|max:10'"
                            data-vv-name="tagId"
                            class="mr-3"
                            ref="tagId"
                          />
                        </v-col>

                        <!-- Key # -->
                        <v-col cols="12" sm="5">
                          <v-text-field
                            v-model="editedKeyTag.keyNum"
                            type="number"
                            label="Key #"
                            hide-details="auto"
                            :error-messages="errors.first('keyNum')"
                            v-validate="editedKeyTag.residentId > 0 ? 'required|numeric|max_value:10000|min_value:1' : 'required|numeric|max_value:10000|min_value:0'"
                            data-vv-name="keyNum"
                          />
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col cols="12" sm="12">

                          <!-- Resident Assignment-->
                          <v-select
                            v-model="editedKeyTag.residentId"
                            :items="selectorResidents"
                            item-value="id"
                            item-text="name"
                            label="Assign to Resident"
                            class="mt-3"
                            hide-details
                            :disabled="editedKeyTag.kioskId <= 0 || selectorResidents.length === 1"
                          />
                        </v-col>
                      </v-row>
                    </form>
                  </v-container>
                </v-card-text>
                <v-card-actions class="pb-3">
                 
                  <v-checkbox
                    v-if="editedKeyTag.id < 0"
                    v-model="isCloseAfterAdd"
                    :color="appColor_Selected"
                     class="ml-3"
                    >
                    <template v-slot:label>
                      <span>Auto-close</span>
                    </template>
                  </v-checkbox>

                  <v-spacer />

                  <v-btn @click="closeDialog" :loading="isSaving" class="rounded-0" large>{{ cancelText }}</v-btn>
                  <v-btn :color="appColor_Dark" dark @click="save" :loading="isSaving" class="rounded-0 ml-4" large>Save</v-btn>
               
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar-items>
        </v-toolbar>

          <!-- Filters -->
          <v-row no-gutters class="mx-3 mt-1">

          <!-- Search -->
          <v-col cols="12" md="12">
            <v-text-field
              v-model="search"
              append-icon="search"
              :label="getExpression('Label', 'Search')"
              single-line
              hide-details
              :disabled="isLoading"
              clearable
              class="mb-3"
            />
          </v-col>
        </v-row>
      </template>

      <template v-slot:item.residentName="{ item }">
        <v-icon v-if="item.residentId === null">horizontal_rule</v-icon>
        <span v-else>{{ item.residentName }}</span>
      </template>

      <template v-slot:item.action="{ item }">
        <span  style="white-space: nowrap">
          <v-icon @click="edit(item)" class="mr-4">edit</v-icon>
          <v-icon v-if="item.residentId === null" @click="remove(item)">
            delete
          </v-icon>
          <v-icon v-else color="grey lighten-1">delete</v-icon>
        </span>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import appService from '@/app.service'
import AppBackButton from '@/components/App/AppBackButton'
import KioskCombobox from '@/components/KioskCombobox'
import { mapGetters } from 'vuex'
import { AppColor, KioskTypeId } from '@/config.js'

export default {
  name: 'KioskKeyTagMapper',
  components: {
    AppBackButton,
    KioskCombobox,
  },
  $_veeValidate: {
    validator: 'new'
  },
  data() {
    return {
      // colors
      appColor_Medium: AppColor.Medium,
      appColor_Dark: AppColor.Dark,
      appColor_Progress: AppColor.Progress,
      appColor_Selected: AppColor.Selected,
      appColor_Abby: AppColor.KioskTypeAbbyDark,

      kioskTypeId_Abby: KioskTypeId.Abby,

      isLoading: true,
      isSaving: false,
      isDeleting: false,
      isCloseAfterAdd: true,
      search: '',
      editDialog: false,
      keyTags: [],
      kiosks: [],
      residents: [],

      dictionary : {
        custom: {
          kioskId: {
            min_value: 'Selection is required'
          },
          tagId: {
            required: () => 'Value is required',
            min: 'Value must contain 10 characters',
            max: 'Value must contain 10 characters'
          },
          keyNum: {
            required: () => 'Value is required',
            max_value: 'Value cannot exceed 10000',
            min_value: 'Invalid Key #',
            numeric: 'Value must be positive and numeric'
          },
        }
      },

      editedIndex: -1,
      editedKeyTag: {
        id: -1,
        kioskId: 0,
        tagId: null,
        keyNum: null,
        residentId: null,
        facilityId: null
      },
      defaultKeyTag: {
        id: -1,
        kioskId: 0,
        tagId: null,
        keyNum: null,
        residentId: null,
        facilityId: null
      },
      restoreKeyTag: {
        id: -1,
        kioskId: 0,
        tagId: null,
        keyNum: null,
        residentId: null,
        facilityId: null
      },
      headers: [
        {
          text: 'Kiosk Id',
          value: 'kioskId',
          align: 'left',
          width: '10%',
          class: 'pr-0'
        },
        {
          text: 'S/N',
          value: 'kioskSn',
          align: 'left',
          width: '10%',
          class: 'pr-0'
        },
        {
          text: 'Facility',
          value: 'facilityName',
          align: 'left',
          width: '20%',
          class: 'pr-0'
        },
        {
          text: 'Tag Id',
          value: 'tagId',
          align: 'left',
          width: '15%',
          class: 'pr-0'
        },
        {
          text: 'Key #',
          value: 'keyNum',
          align: 'left',
          width: '13%',
          class: 'pr-0'
        },
        {
          text: 'Assigned To',
          value: 'residentName',
          align: 'left',
          width: '20%',
          class: 'pr-0'
        },
        {
          value: 'action',
          sortable: false,
          align: 'right',
          width: '12%'
        }
      ],
    }
  },
  computed: {
    ...mapGetters(['profile', 'locale']),
    noDataText () {
      if (this.isLoading) return this.getExpression('Alert', 'OneMoment')

      if (this.keyTags.length === 0) {
        return 'No Key/Tag mappings found'
      }
      return ''
    },
    formTitle () {
      return this.editedIndex < 0 ? 'Create Mapping' : 'Edit Mapping'
    },
    toolbarColor () {
      return this.appColor_Medium
    },
    toolbarColorDark () {
      return this.appColor_Dark
    },
    cancelText () {
      return this.editedKeyTag.id > 0
        ? 'Cancel'
        : 'Close'
    },
    selectorKiosks () {
      const self = this

      const result = self.kiosks
        .map(k => {
          const facilities = self.facilities.filter(f => f.id === k.facilityId)
          const facilityName = facilities.length > 0
            ? facilities[0].name
            : null

          const kioskDesc = facilityName !== null
            ? `${k.serialNumber} (${facilityName})`
            : k.serialNumber

          return {
            id: k.id,
            name: kioskDesc,
            typeId: k.typeId
          }
        })

      return result
    },
    selectorResidents () {
      if (this.editedKeyTag.kioskId <= 0) return []

      const self = this
      const kioskId = self.editedKeyTag.kioskId
      const residentIds = self.keyTags
        .filter(kt => kt.residentId !== null)
        .map(kt => kt.residentId)

      var selectorResidents = []
      selectorResidents.push({
        id: -1,
        name: '(Unassigned)'
      })

      const result = selectorResidents.concat(self.residents
        .filter(r => r.kioskId === kioskId && !residentIds.includes(r.id) || r.id === self.editedKeyTag.residentId)
        .map(r => { 
          return {
            id: r.id,
            name: r.name,
          }
        }))

      return result
    },

    // styles
    dialogWidth () {
      return '400px'
    },
    titleClass () {
      return this.isSmallDevice
        ? 'body-2'
        : ''
    }
  },
  methods: {
    loadData () {
      const self = this

      appService.getKioskKeyTagsForMapper().then(data => {
        if (data.errorSeverity > 0) {
          self.handleError(data)
        } else {
          self.kiosks = data.kiosks
          self.residents = data.residents
          self.facilities = data.facilities

          self.keyTags = data.keyTags.map(kkt => { return {
            id: kkt.id,
            kioskId: kkt.kioskId,
            tagId: kkt.tagId,
            keyNum: kkt.keyNum,
            residentId: kkt.residentId,
            facilityId: kkt.facilityId,
            kioskSn: self.getKioskSn(kkt.kioskId),
            facilityName: self.getFacilityName(kkt.facilityId),
            residentName: self.getResidentName(kkt.residentId)
          }})

          self.isLoading = false
        }
      })
    },

    edit (item) {
      const self = this
      self.editedIndex = self.keyTags.findIndex(kt => kt.id === item.id)
      self.editedKeyTag = Object.assign({}, item)
      self.restoreKeyTag = Object.assign({}, item)
      if (self.editedKeyTag.residentId === null)
        self.editedKeyTag.residentId = -1

      self.editDialog = true

      // force the kiosk combobox to initialize its watcher
      self.editedKeyTag.kioskId = 0
      setTimeout(()=> {
        self.editedKeyTag.kioskId = item.kioskId
      }, 100)
    },

    save () {
      const self = this
      self.$validator.resume()
      self.$validator.validateAll().then(valid => {
        if (valid) {
          self.isSaving = true
          
          const isEditMode = self.editedIndex > -1

          // edit mode
          if (isEditMode) {
            var editedKeyTag = self.keyTags[self.editedIndex]
            Object.assign(editedKeyTag, self.editedKeyTag)
            const editModel = self.getModel(self.editedKeyTag, isEditMode)
            var isError = false;

            // will only do work if required
            appService.updateKioskKeyTag(editedKeyTag.id, editModel).then(data => {
              if (data.errorSeverity !== null) {
                isError = true;
                self.handleError(data)
                Object.assign(self.keyTags[self.editedIndex], self.restoreKeyTag)
              } else {
                self.assignResident().then(result => {
                  if (result.errorSeverity !== null) {
                    isError = true;
                    self.handleError(result)
                  } else {
                    // if the kiosk was changed need to lookup the facilityId
                    // since the update result will contain the last one it received
                    const facilityId = self.getFacilityId(data.keyTag.kioskId)
                    self.keyTags[self.editedIndex].facilityId = facilityId
                    self.keyTags[self.editedIndex].kioskSn = self.getKioskSn(data.keyTag.kioskId)
                    self.keyTags[self.editedIndex].facilityId = facilityId
                    self.keyTags[self.editedIndex].facilityName = self.getFacilityName(facilityId)
                    self.keyTags[self.editedIndex].residentId = data.keyTag.residentId > 0 ? data.keyTag.residentId : null
                    self.keyTags[self.editedIndex].residentName = self.getResidentName(data.keyTag.residentId)
                    self.closeDialog()
                  }
                  self.isSaving = false
                  self.$validator.pause()
                }).catch(e => { self.handleCriticalError(e) })
              }
              if (isError) {
                self.isSaving = false
                self.$validator.pause()
              }
            }).catch(e => { self.handleCriticalError(e) })

          // add mode
          } else {
            const addModel = self.getModel(self.editedKeyTag, isEditMode)

            appService.addKioskKeyTag(addModel).then(data => {
              if (data.errorSeverity !== null) {
                self.handleError(data)
              } else {
                var newKeyTag = Object.assign({}, data.keyTag)
                self.editedKeyTag.id = newKeyTag.id

                // add result doesn't contain facilityId or residentId
                // initialize their values using the lookup collections
                newKeyTag.facilityId = self.getFacilityId(newKeyTag.kioskId)
                newKeyTag.residentId = self.editedKeyTag.residentId > 0
                  ? self.editedKeyTag.residentId
                  : null

                // will only do work if required
                self.assignResident().then(result => {
                  if (result.errorSeverity !== null) {
                    self.handleError(result)
                  } else {
                    newKeyTag.kioskSn = self.getKioskSn(newKeyTag.kioskId)
                    newKeyTag.residentName = self.getResidentName(newKeyTag.residentId)
                    newKeyTag.facilityName = self.getFacilityName(newKeyTag.facilityId)

                    self.keyTags.unshift(newKeyTag)

                    if (self.isCloseAfterAdd) {
                      self.closeDialog()
                    } else {
                      self.resetEditedKeyTag()
                      self.editedKeyTag.kioskId = newKeyTag.kioskId
                      self.editedKeyTag.residentId = -1
                    }
                  }
                }).catch(e => { self.handleCriticalError(e) })
              }
              self.isSaving = false
              self.$validator.pause()
            }).catch(e => { self.handleCriticalError(e) })
          }
        }
      })
    },

    remove (item) {
      const self = this
      const index = self.keyTags.indexOf(item)
      const facilityName = !self.isNullOrUndefined(item.facilityName)
        ? `<b>${item.facilityName}</b>`
        : '<i>(None Assigned)</i>'

      self.$confirm.show({
        title: 'Delete Mapping?',
        text: `Facility: ${facilityName}<br />` +
              `Kiosk: <b>${item.kioskSn}</b> (${item.kioskId})<br /><br />` +
              `Tag Id: <b>${item.tagId}</b><br />` +
              `Key #: <b>${item.keyNum}</b>`,
        width: 320,
        onConfirm: () => {
          self.isDeleting = true
          appService.deleteKioskKeyTag(item.id).then(data => {
            if (data.errorSeverity !== null) {
              self.handleError(data)
            } else {
              self.keyTags.splice(index, 1)
            }
            self.isDeleting = false
          }).catch(e => { self.handleCriticalError(e) })
        }
      })
    },

    assignResident () {
      return new Promise(resolve => {
        const self = this

        // if nothing changed then return
        if (self.editedKeyTag.residentId === self.restoreKeyTag.residentId)
          resolve({ errorSeverity: null })
        
        // set parameters accordingly depending on if an assignment or unassignment
        const residentId = self.editedKeyTag.residentId > 0
          ? self.editedKeyTag.residentId 
          : self.restoreKeyTag.residentId

        const keyTagId = self.editedKeyTag.residentId > 0
          ? self.editedKeyTag.id
          : 0

        if (residentId > 0) {
          const updateByUserId = self.profile.id.replace('auth0|', '')
          appService.assignResidentKioskKeyTag(residentId, keyTagId, updateByUserId, self.locale).then(data => {
            resolve(data)
          }).catch(e => { self.handleCriticalError(e) })
        } else { 
          resolve( { errorSeverity: null })
        }
      })
    },

    getModel (kkt) {
      // cast Key # as Int
      var keyNum = null
      if (kkt.keyNum !== null) {
        keyNum = isNaN(kkt.keyNum) ? null : parseInt(kkt.keyNum)
      }

      return {
        id: kkt.id,
        kioskId: kkt.kioskId,
        tagId: kkt.tagId,
        keyNum: keyNum,
        residentId: kkt.residentId,
        facilityId: kkt.facilityId
      }
    },

    getKioskSn (kioskId) {
      const kiosks = this.kiosks.filter(k => {
        return k.id === kioskId
      })
      return kiosks.length > 0
        ? kiosks[0].serialNumber
        : null
    },

    getFacilityId(kioskId) {
      const kiosks = this.kiosks.filter(k => k.id === kioskId)
      return kiosks.length > 0 ? kiosks[0].facilityId : null
    },

    getFacilityName (facilityId) {
      const facilities = this.facilities.filter(f => {
        return f.id === facilityId
      })
      return facilities.length > 0
        ? facilities[0].name
        : null
    },

    getResidentName (residentId) {
      const residents = this.residents.filter(r => {
        return r.id === residentId
      })
      return residents.length > 0
        ? residents[0].name
        : null
    },

    customFilter (value, search, item) {
      const kioskId = item.kioskId.toString() ?? ''
      const kioskSn = item.kioskSn ?? ''
      const facilityName = item.facilityName ?? ''
      const residentName = item.residentName ?? ''

      return (
        (value != null &&
          search != null &&
          kioskId
            .toLowerCase()
            .indexOf(search.toLowerCase()) !== -1 ||
          kioskSn
            .toLowerCase()
            .indexOf(search.toLowerCase()) !== -1 ||
          facilityName
            .toLowerCase()
            .indexOf(search.toLowerCase()) !== -1 ||
          residentName
            .toLowerCase()
            .indexOf(search.toLowerCase()) !== -1 ||
          item.tagId
            .toString()
            .toLowerCase()
            .indexOf(search.toLowerCase()) !== -1)
      )
    },

    closeDialog () {
      this.editDialog = false
      setTimeout(() => {
        this.editedIndex = -1
        this.resetEditedKeyTag()
      }, 300)
    },

    resetEditedKeyTag () {
      this.editedKeyTag = Object.assign({}, this.defaultKeyTag)
      this.editedIndex = -1
      this.$validator.reset()
    },

    handleError (result) {
      this.$error.show({
        title: result.errorSeverity === 0
          ? 'Validation Failed'
          : 'Internal Error',
        text: result.errorMessage,
        severity: result.errorSeverity
      })
    }
  },
  created() {
    this.loadData()
  },
  beforeDestroy() {
    this.$validator.pause()
  },
  mounted() {
    this.$validator.localize('en', this.dictionary)
    this.$validator.reset()
  },
  watch: {
    editDialog (val) {
      if (val) {
        const self = this
        setTimeout(() => {
          if (self.editedIndex >= 0)
            self.$refs.tagId.focus()
          else {
            self.editedKeyTag.kioskId = -1
            self.editedKeyTag.residentId = -1
            self.$refs.kiosk.focus()
            self.$validator.reset()
            self.$validator.pause()
          }
        }, 100)
      } else {
        this.closeDialog()
      }
    }
  }
}
</script>



